import { Component } from '@angular/core';
import { WebsiteAuditService } from '../website-audit.service';
import { Subscription } from 'rxjs';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NotificationService } from 'src/app/core/services/notification.service';
import { AddTaskComponent } from '../modals/add-task/add-task.component';
import { Router } from '@angular/router';
import { SidenavService } from 'src/app/sidenav/sidenav.service';
import { ConfirmationComponent } from 'src/app/modals/confirmation/confirmation.component';
import { SeoService } from 'src/app/seo/seo.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';

@Component({
  selector: 'app-management',
  templateUrl: './management.component.html',
  styleUrls: ['./management.component.css'],
})
export class ManagementComponent {
  constructor(
    public router: Router,
    private websiteAuditService: WebsiteAuditService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private sidenavService: SidenavService,
    private seoService: SeoService,
    private localStorageService: LocalStorageService,
  ) {}

  confirmDialogRef: MatDialogRef<ConfirmationComponent>;
  addTaskDialogRef: MatDialogRef<AddTaskComponent>;
  subs: Subscription = new Subscription();

  isLoading: boolean = false;
  firstLoader: boolean = false;
  tasks: any = [];

  selectedTask: any = null;

  areThereTasksInProgress: boolean = false;

  private timeout: any;
  private timeoutSeconds: number = 30;

  ngOnInit(): void {
    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((res: any) => {
        if (res) {
          this.selectedTask = res;
        }
      }),
    );

    this.subs.add(
      this.websiteAuditService.getAuditTasks().subscribe((res: any) => {
        this.tasks = res;
        this.getStatusOfProjects();
      }),
    );

    const addAuditClicked = sessionStorage.getItem('addAudit');
    if (addAuditClicked) {
      this.openAddTaskModal();
    }
    sessionStorage.removeItem('addAudit');
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  getDomainFromUrl(url: string): string | null {
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;

      const { hostname } = new URL(normalized);
      return hostname.replace(/^www\./, '');
    } catch {
      return null;
    }
  }
  getStatusOfProjects() {
    const domains: any = [];

    this.tasks.forEach((element: any) => {
      domains.push(this.getDomainFromUrl(element?.url));
    });

    this.seoService.getStatusOfProject({ domains: domains }).subscribe({
      next: (res: any) => {
        this.firstLoader = false;

        const progresses = res?.progresses || [];

        progresses.forEach((progress: any) => {
          const task = this.tasks.find((task: any) =>
            task?.url.includes(progress?.domain),
          );

          if (task?.averageStatusBar <= 100) {
            task['status'] = 'Pending';
          }
        });
      },
      error: (err) => {
        this.firstLoader = false;
      },
    });
  }

  openTask(task: any, changeTab: boolean = true) {
    this.isLoading = true;
    this.subs.add(
      this.websiteAuditService.getAuditTask(task?.taskId).subscribe({
        next: (res) => {
          const domainFromUrl = this.getDomainFromUrl(res?.url);

          if (!domainFromUrl || res?.type === 'Crawler') {
            this.isLoading = false;
            this.websiteAuditService.updateSelectedAuditTask(res);
            if (changeTab) {
              const tabIndex = res?.type === 'Crawler' ? 7 : 0;
              this.sidenavService.updateActiveTabIndex(tabIndex);
            }
            return;
          }

          this.subs.add(
            this.seoService
              .getStatusOfProject({ domain: domainFromUrl })
              .subscribe({
                next: (organicProgress: any) => {
                  this.isLoading = false;
                  const crawlStatus =
                    organicProgress?.progresses?.[0]?.crawl_status || '';

                  this.websiteAuditService.updateSelectedAuditTask({
                    ...res,
                    organicProgress: {
                      progress: organicProgress?.averageStatusBar,
                      status: crawlStatus,
                    },
                  });

                  if (changeTab) {
                    this.sidenavService.updateActiveTabIndex(0);
                  }
                },
                error: (err) => {
                  this.isLoading = false;
                  this.websiteAuditService.updateSelectedAuditTask({
                    ...res,
                    organicProgress: {
                      progress: 0,
                      status: 'failed',
                    },
                  });

                  if (changeTab) {
                    this.sidenavService.updateActiveTabIndex(0);
                  }
                },
              }),
          );
        },
        error: (err) => {
          this.isLoading = false;
          this.notificationService.error(err?.error?.message);
        },
      }),
    );
  }

  openAddTaskModal() {
    this.addTaskDialogRef = this.dialog.open(AddTaskComponent, {
      width: '430px',
      data: {},
    });
    this.addTaskDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.notificationService.info('Audit added successfully!');
        this.websiteAuditService.fetchAuditTasks();
      }
    });
  }

  rerunAudit(item: any) {
    this.confirmDialogRef = this.dialog.open(ConfirmationComponent, {
      width: '430px',
      data: {
        header: 'Rerun Audit',
        content: 'Are you sure you want to rerun this audit?',
      },
    });

    this.subs.add(
      this.confirmDialogRef.afterClosed().subscribe((result) => {
        if (result == 1) {
          item['auditRerunning'] = true;
          this.subs.add(
            this.websiteAuditService.rerunAudit(item).subscribe({
              next: (res: any) => {
                item['auditRerunning'] = false;
                this.notificationService.info('Started rerunning audit');
                this.websiteAuditService.fetchAuditTasks();
              },
              error: (err: any) => {
                item['auditRerunning'] = false;
                this.notificationService.error(err?.error?.message);
              },
            }),
          );
        }
      }),
    );
  }

  openDeleteAudit(item: any) {
    this.confirmDialogRef = this.dialog.open(ConfirmationComponent, {
      width: '430px',
      data: {
        header: 'Delete Audit',
        content: 'Are you sure you want to delete this audit?',
      },
    });

    this.subs.add(
      this.confirmDialogRef.afterClosed().subscribe((result) => {
        if (result == 1) {
          this.subs.add(
            this.websiteAuditService.deleteAudit(item.taskId).subscribe({
              next: (res: any) => {
                this.notificationService.info('Audit deleted successfully!');
                this.websiteAuditService.fetchAuditTasks();
              },
              error: (err: any) => {
                this.notificationService.error(err?.error?.message);
              },
            }),
          );
        }
      }),
    );
  }

  private getHubspotName(): string {
    const email: string = this.localStorageService.get('email') || '';

    if (email.includes('brooke')) {
      return 'brooke-eichhorn';
    } else if (email.includes('valon') || email.includes('flamur')) {
      return 'valon-xhafa';
    } else if (email.includes('dahoud') || email.includes('daoud')) {
      return 'marcel-dahoud';
    } else {
      return 'patrick-fischer1';
    }
  }

  shareAudit(item: any) {
    this.subs.add(
      this.websiteAuditService
        .getAuditTaskShareableToken({ taskId: item.taskId })
        .subscribe({
          next: (res: any) => {
            navigator.clipboard.writeText(
              `${window.location.origin}/share/website-audit/${res.token}?hubspot=${this.getHubspotName()}`,
            );
            this.notificationService.info('Shareable url copied!');
          },
          error: (err: any) => {
            this.notificationService.error(err?.error?.message);
          },
        }),
    );
  }
}
